import React from "react";
import { Box } from "@leagueplatform/genesis-core";
import {
  MasonrySmallCardRenderer,
  useInvokeAction,
  type MasonryDeeplinkAction,
} from "@leagueplatform/masonry-renderers";

type MasonrySmallCardRendererProps = React.ComponentProps<
  typeof MasonrySmallCardRenderer
>;

/**
 * QUEST-2626 — the SDK `smallCard` renderer only wires deeplink onClicks (those
 * with `payload.url`), so custom actions like `startSchedulingFlow` render
 * inert. This override delegates to the SDK renderer for deeplink / no-onClick
 * cases, and for a url-less action wraps the card in an accessible click target
 * that dispatches it. Remove once the SDK wires non-url onClicks natively.
 */
export const MasonrySmallCardRendererOverride = (
  props: MasonrySmallCardRendererProps
) => {
  const { onClick } = props;
  const invokeAction = useInvokeAction();

  const isCustomAction =
    !!onClick && !(onClick as MasonryDeeplinkAction).payload?.url;

  if (!isCustomAction) {
    return <MasonrySmallCardRenderer {...props} />;
  }

  return (
    <Box
      as="button"
      type="button"
      onClick={(event: React.MouseEvent) => invokeAction({ event, ...onClick })}
      css={{
        all: "unset",
        display: "block",
        width: "100%",
        cursor: "pointer",
        borderRadius: "$medium",
        "&:focus-visible": {
          outline: "2px solid $interactiveFocusInner",
          outlineOffset: "2px",
        },
      }}
    >
      <MasonrySmallCardRenderer {...props} onClick={undefined} />
    </Box>
  );
};
